import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useLocation } from '@xing-com/crate-core-router';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import { TextButton } from '@xing-com/crate-lebenslauf-button';
import {
  ProgressBar,
  TemplateZoom,
} from '@xing-com/crate-lebenslauf-components';
import {
  isValidColor,
  isValidTemplate,
} from '@xing-com/crate-lebenslauf-helpers';
import {
  useBasePath,
  useCoverLetterActions,
  useLastCoverLetter,
  useReadVitaReactiveVariable,
  useVitaTracking,
} from '@xing-com/crate-lebenslauf-hooks';
import {
  CoverLetterAIForm,
  Preferences,
  Preview,
  Templates,
} from '@xing-com/crate-lebenslauf-xinglets';
import { NoSSR } from '@xing-com/crate-utils-no-ssr';
import { IconArrowDownward, IconGridView } from '@xing-com/icons';

import {
  ColorPickerStyled,
  Container,
  Footer,
  GlobalStyles,
  StyledLebenslaufButton,
  StyledPagination,
  StyledPreviewButton,
  StyledPreviewButtonText,
  StyledZoomedPreviewWrapper,
} from './cover-letter.styled';

type LocationState = {
  color?: string;
  template?: string;
};

const extractThemeFromHash = (hash?: string): LocationState => {
  const [template, color] = hash ? hash.substring(1).split('-') : [];
  return { template, color };
};

export const CoverLetter: React.FC = () => {
  const basePath = useBasePath();
  const { trackInteraction, trackPageView, trackWithNWT, getTrackingPrefixes } =
    useVitaTracking({
      pageName: 'editor',
    });
  const isTooSmallForZoom = useMatchMedia('(max-aspect-ratio: 8/5)'); // be careful: during SSR it will be always true
  const { hash, pathname } = useLocation();
  const { template, color } = extractThemeFromHash(hash);
  const { coverLetterThemeActions } = useCoverLetterActions();
  const { showAnschreibenBanner } = useReadVitaReactiveVariable();
  const [iconOnlyPreview, setIconOnlyPreview] = useState(false);
  const [isShown, setIsShown] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [pageVisible, setPageVisible] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(0);
  const togglePreferences = (): void => {
    setShowPreferences(!showPreferences);
  };

  useLastCoverLetter();

  React.useEffect(() => {
    trackPageView({
      PropContextDimension1: `${getTrackingPrefixes().cover_letter}_editor_ai`,
    });
    trackWithNWT({
      flags: `anschreiben_ai_true`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackPageView, trackWithNWT]);

  const togglePreview = (): void => {
    setIsShown(!isShown);
    if (!isShown) {
      trackInteraction(
        `${getTrackingPrefixes().cover_letter}_editor_change_template`,
        {
          PropContextDimension1: `${getTrackingPrefixes().cover_letter}_editor_ai`,
        }
      );
      trackWithNWT({
        event: 'clicked',
        element: 'change_template',
        flags: `anschreiben_ai_true`,
      });
    }
  };
  const handleSectionVisibilityChange = (): void => {
    // transition to the icon-only button - 600px scroll offset is a fitting position for the length of our form
    setIconOnlyPreview(window.scrollY > 600);
  };

  const trackLebenslaufClick = (): void => {
    trackWithNWT({
      event: 'opened',
      element: `lebenslauf`,
      flags: `anschreiben_ai_true`,
    });
  };

  useEffect(() => {
    const uppercaseTemplate = template?.toUpperCase();
    const uppercaseColor = color?.toUpperCase();

    if (isValidTemplate(uppercaseTemplate)) {
      coverLetterThemeActions({
        type: 'change-template',
        value: uppercaseTemplate,
      });
    }

    if (isValidColor(uppercaseColor)) {
      coverLetterThemeActions({ type: 'change-color', value: uppercaseColor });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color, template]);

  useEffect(() => window.scrollTo(0, 0), [pathname]);

  return (
    <>
      <GlobalStyles $showAnschreibenBanner={!!showAnschreibenBanner} />

      <ProgressBar showCVLink togglePreferences={togglePreferences} />

      {isShown ? (
        <Templates isShown={isShown} onToggle={togglePreview} />
      ) : null}

      <CoverLetterAIForm
        onSectionVisibilityChange={handleSectionVisibilityChange}
      />

      <Container $hasBanner={!!showAnschreibenBanner}>
        <NoSSR>
          <ColorPickerStyled />
          <StyledZoomedPreviewWrapper
            $zoom={isTooSmallForZoom ? 1 : 1 + zoom / 10}
          >
            <Preview pageVisible={pageVisible} setTotalPages={setTotalPages} />
          </StyledZoomedPreviewWrapper>
          <Footer>
            <TextButton
              icon={IconGridView}
              onClick={togglePreview}
              data-qa="lebenslauf-templates-button"
            >
              <FormattedMessage id="CRATE_LEBENSLAUF_CHANGE_TEMPLATE" />
            </TextButton>
            <TemplateZoom
              onZoomIn={() => {
                if (zoom < 6) {
                  setZoom(zoom + 1);
                }
              }}
              onZoomOut={() => {
                if (zoom > 0) {
                  setZoom(zoom - 1);
                }
              }}
              zoom={zoom}
            />
            <StyledPagination
              pageVisible={pageVisible}
              totalPages={totalPages}
              setPageVisible={setPageVisible}
            />
          </Footer>
        </NoSSR>
      </Container>

      <StyledLebenslaufButton
        variant="proJobs"
        size="large"
        icon={IconArrowDownward}
        onClick={trackLebenslaufClick}
        data-qa="lebenslauf-button"
        to={`${basePath}/neu?sc_o=${getTrackingPrefixes().cover_letter}_editor_lebenslauf_click`}
      >
        <FormattedMessage id="CRATE_VITA_TEMPLATE_TAG" />
      </StyledLebenslaufButton>

      <StyledPreviewButton
        variant="primary"
        onClick={togglePreview}
        size="medium"
        $iconOnlyPreview={iconOnlyPreview}
      >
        <IconGridView width={24} height={24} />
        <StyledPreviewButtonText $iconOnlyPreview={iconOnlyPreview}>
          <FormattedMessage id="CRATE_LEBENSLAUF_PREVIEW_BUTTON" />
        </StyledPreviewButtonText>
      </StyledPreviewButton>

      <Preferences
        show={showPreferences}
        togglePreferences={togglePreferences}
      />
    </>
  );
};
